import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { DrawerHeader } from "./Drawer.Header";
import { styles } from "./styles";
import { DrawerProps } from "./types";

/**
 * Panel component for the Drawer.
 * Renders the drawer panel with header, content, and optional footer.
 * @internal
 */
export const DrawerPanel = forwardRef<
  HTMLDivElement,
  DrawerProps & {
    setHeaderHeight?: (height: string) => void;
    setFooterHeight?: (height: string) => void;
  }
>(
  (
    {
      isOpen,
      position = "right",
      pushContent,
      onClose,
      title,
      titleIcon,
      headerButtons,
      noHeader = false,
      footer,
      children,
      noPadding = false,
      noContentPadding = false,
      closeOnFooterClick,
      theme,
      setHeaderHeight,
      setFooterHeight
    },
    ref
  ) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const footerRef = useRef<HTMLDivElement | null>(null);
    const titleId = useMemo(
      () => `kit-drawer-title-${crypto.randomUUID()}`,
      []
    );

    const handleOnClose = useCallback(() => {
      onClose?.();
    }, [onClose]);

    // Attach click listener to footer if closeOnFooterClick is true
    useEffect(() => {
      const currentElement = footerRef.current;

      if (closeOnFooterClick && currentElement) {
        currentElement.addEventListener("click", handleOnClose);
      }

      return () => {
        if (currentElement) {
          currentElement.removeEventListener("click", handleOnClose);
        }
      };
    }, [handleOnClose, closeOnFooterClick]);

    if (!isOpen) {
      return null;
    }
    const hasPushedContent = !!pushContent;
    return (
      <div
        className={`kit-drawer-panel${theme ? ` ${theme}-theme` : ""}`}
        role="dialog"
        aria-modal={!pushContent}
        tabIndex={-1}
        ref={current => {
          panelRef.current = current;
          if (!ref) return;
          if (typeof ref === "function") {
            ref(current);
          } else {
            ref.current = current;
          }
        }}
        css={[
          styles.panel.base,
          styles.panel.positionOffset[position],
          !hasPushedContent &&
            styles.panel.positionWithoutPushContent[position],
          hasPushedContent && styles.panel.withPushContent
        ]}
      >
        {!noHeader && (
          <DrawerHeader
            ref={current =>
              current &&
              setHeaderHeight?.(
                `${Math.round(current?.getBoundingClientRect().height || 0)}px`
              )
            }
            titleIcon={titleIcon}
            headerButtons={headerButtons}
            onClose={onClose}
            titleId={titleId}
          >
            {title}
          </DrawerHeader>
        )}
        <div
          className="kit-drawer-content"
          role="region"
          aria-labelledby={title ? titleId : undefined}
          css={[
            !noPadding && !noContentPadding && styles.content.padded,
            hasPushedContent && styles.content.withPushContent,
            styles.content.base
          ]}
        >
          {children}
        </div>

        {footer && (
          <div
            className="kit-drawer-footer"
            ref={current => {
              footerRef.current = current;
              if (current) {
                setFooterHeight?.(
                  `${Math.round(current?.getBoundingClientRect().height || 0)}px`
                );
              }
            }}
            css={[styles.footer]}
          >
            {footer}
          </div>
        )}
      </div>
    );
  }
);
